import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import accommodationRoomTypeObject from '@/entities/accommodationRoomType/interface'
import accommodationAvailableAddonObject from '@/entities/accommodationAvailableAddon/interface'

import {
  ALL_ACCOMMODATIONS_QUERY,
  SINGLE_ACCOMMODATION_QUERY,
  ACCOMMODATIONS_DEPENDENTS_QUERY,
  CREATE_ACCOMMODATION_MUTATION,
  UPDATE_ACCOMMODATION_MUTATION,
  DELETE_ACCOMMODATION_MUTATION,
  FORCE_DELETE_ACCOMMODATION_MUTATION,
  ADD_ACCOMMODATION_ROOM_TYPES_MUTATION,
  REMOVE_ACCOMMODATION_ROOM_TYPES_MUTATION,
  CREATE_ACCOMMODATION_ROOM_TYPES_MUTATION,
  DELETE_ACCOMMODATION_ROOM_TYPES_MUTATION,
  UPDATE_ACCOMMODATION_ROOM_TYPES_ORDER_MUTATION,
  ADD_ACCOMMODATION_AVAILABLE_ADDONS_MUTATION,
  REMOVE_ACCOMMODATION_AVAILABLE_ADDONS_MUTATION,
  CREATE_ACCOMMODATION_AVAILABLE_ADDONS_MUTATION,
  DELETE_ACCOMMODATION_AVAILABLE_ADDONS_MUTATION,
  UPDATE_ACCOMMODATION_AVAILABLE_ADDONS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const accommodationRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_ACCOMMODATION_QUERY, variables).then((data: any) => {
      return hydrateObject(data.accommodation)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_ACCOMMODATIONS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.accommodations)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_ACCOMMODATION_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.accommodation),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_ACCOMMODATIONS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.accommodations),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(ACCOMMODATIONS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([ACCOMMODATIONS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_ACCOMMODATION_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.accommodation)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_ACCOMMODATION_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.accommodation)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_ACCOMMODATION_MUTATION, variables).then((data: any) => {
      return data.deleteAccommodation
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_ACCOMMODATION_MUTATION, variables).then((data: any) => {
      return data.forceDeleteAccommodation
    })
  },

  assignRoomTypes: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        roomTypeIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_ACCOMMODATION_ROOM_TYPES_MUTATION, variables)
  },

  unassignRoomTypes: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        roomTypeIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_ACCOMMODATION_ROOM_TYPES_MUTATION, variables)
  },

  createRoomTypes: async (id: string, items: Array<accommodationRoomTypeObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        roomTypes: items,
      },
    }
    return graphQLClient.request(CREATE_ACCOMMODATION_ROOM_TYPES_MUTATION, variables)
  },

  deleteRoomTypes: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        roomTypeIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_ACCOMMODATION_ROOM_TYPES_MUTATION, variables)
  },

  updateRoomTypesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        roomTypeIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_ACCOMMODATION_ROOM_TYPES_ORDER_MUTATION, variables)
  },

  assignAvailableAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        availableAddonIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_ACCOMMODATION_AVAILABLE_ADDONS_MUTATION, variables)
  },

  unassignAvailableAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        availableAddonIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_ACCOMMODATION_AVAILABLE_ADDONS_MUTATION, variables)
  },

  createAvailableAddons: async (id: string, items: Array<accommodationAvailableAddonObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        availableAddons: items,
      },
    }
    return graphQLClient.request(CREATE_ACCOMMODATION_AVAILABLE_ADDONS_MUTATION, variables)
  },

  deleteAvailableAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        availableAddonIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_ACCOMMODATION_AVAILABLE_ADDONS_MUTATION, variables)
  },

  updateAvailableAddonsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        availableAddonIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_ACCOMMODATION_AVAILABLE_ADDONS_ORDER_MUTATION, variables)
  },
}

export default accommodationRepository
